import type { RequestConfig, RequestOptions } from '@@/plugin-request/request';
import { getLocale, history } from '@umijs/max';
import { message, Modal, notification } from 'antd';
import React from 'react';
import { useAuthStore } from '@/stores/authStore';
import { getAuthToken } from '@/utils/authPersistence';

/** Matches backend `permissionRequirement` / `permissionRequirementsAnyOf` entries. */
interface AdminPermissionRequirementPayload {
  requiredPermission: string;
  categoryLabel: string;
  resourceLabel: string;
  actionLabel: string;
}

function isPermissionRow(x: unknown): x is AdminPermissionRequirementPayload {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.categoryLabel === 'string' &&
    typeof o.resourceLabel === 'string' &&
    typeof o.actionLabel === 'string'
  );
}

/** One modal listing each alternative permission on its own row (requireAny with 2+ options). */
function showAdminPermissionAlternativesModal(rows: AdminPermissionRequirementPayload[]) {
  const loc = getLocale() || '';
  const lower = loc.toLowerCase();
  const isKo = lower.startsWith('ko');
  const isZh = lower.startsWith('zh');
  const title = isKo ? '권한 필요' : isZh ? '需要权限' : 'Permission required';
  const intro = isKo
    ? '아래 권한 중 하나가 있어야 합니다.'
    : isZh
      ? '需要以下权限之一。'
      : 'You need one of the following permissions:';

  Modal.warning({
    title,
    width: 560,
    content: React.createElement(
      'div',
      null,
      React.createElement(
        'p',
        { style: { marginBottom: 12, color: 'rgba(0, 0, 0, 0.65)' } },
        intro,
      ),
      ...rows.map((p, idx) =>
        React.createElement(
          'div',
          {
            key: `${p.requiredPermission}-${idx}`,
            style: {
              padding: '10px 12px',
              borderBottom: idx < rows.length - 1 ? '1px solid #f0f0f0' : undefined,
              fontSize: 14,
            },
          },
          `${p.categoryLabel} › ${p.resourceLabel} (${p.actionLabel})`,
        ),
      ),
    ),
    okText: isKo ? '확인' : isZh ? '确定' : 'OK',
  });
}

// Map the umi locale code to the short `lang` value the backend expects.
const localeToLang = (locale: string): string => {
  if (!locale) return 'en';
  const lower = locale.toLowerCase();
  if (lower.startsWith('ko')) return 'ko';
  if (lower.startsWith('zh')) return 'zh';
  return 'en';
};

enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

interface ResponseStructure {
  success?: boolean;
  status?: string;
  statusCode?: number;
  data?: unknown;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
  message?: string;
}

const sanitizeParams = (params: Record<string, unknown> = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );

let isHandling401 = false;

export const errorConfig: RequestConfig = {
  timeout: 15000,
  errorConfig: {
    errorThrower: (res) => {
      const response = res as ResponseStructure;

      if (response?.status === 'error' || response?.status === 'failed') {
        const error: Error & { info?: unknown } = new Error(response?.message || 'Request failed');
        error.name = 'BizError';
        error.info = {
          errorCode: response?.statusCode,
          errorMessage: response?.message,
          showType: ErrorShowType.ERROR_MESSAGE,
          data: response?.data,
        };
        throw error;
      }

      const { success, data, errorCode, errorMessage, showType } = response;
      if (success === false) {
        const error: Error & { info?: unknown } = new Error(errorMessage || 'Request failed');
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error;
      }
    },
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) {
        throw error;
      }

      if (
        error?.config?.url?.includes('shipping-rate-rules') &&
        (error?.response?.status === 422 || error?.response?.status === 429)
      ) {
        return;
      }

      if (error.name === 'BizError') {
        const errorInfo = error.info as ResponseStructure | undefined;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          // Always surface the backend's `message` (carried as `errorMessage`)
          // so the user sees what actually went wrong. Use the showType only
          // to pick the toast style; SILENT/REDIRECT still skip the toast.
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              break;
            case ErrorShowType.WARN_MESSAGE:
              if (errorMessage) message.warning(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              if (errorMessage) {
                notification.open({
                  description: errorMessage,
                  message: String(errorCode || ''),
                });
              }
              break;
            case ErrorShowType.REDIRECT:
              break;
            case ErrorShowType.ERROR_MESSAGE:
            default:
              if (errorMessage) message.error(errorMessage);
          }
        }
        return;
      }

      if (error.response) {
        const status = error.response.status;
        const statusText = error.response.statusText;
        const url = error.config?.url;
        const baseURL = error.config?.baseURL;
        const fullUrl = `${baseURL || ''}${url || ''}`;
        const skipErrorHandler = error.config?.skipErrorHandler;
        // Prefer the backend's `message` field whenever the response carries one.
        const backendMessage: string = error.response.data?.message || '';

        if (status === 429 || skipErrorHandler) {
          return;
        }

        if (status === 422) {
          // Validation failures (express-validator → ValidationError) were silently
          // swallowed before — surface the first field error so the user knows why
          // nothing happened.
          const details = error.response.data?.details;
          const detailMessage =
            Array.isArray(details) && details.length > 0 ? details[0]?.message : '';
          const shown = detailMessage || backendMessage;
          if (shown) message.warning(shown);
          return;
        }

        if (process.env.NODE_ENV === 'development') {
          console.error('HTTP Error Response:', { status, statusText, fullUrl, backendMessage });
        }

        if (status === 401) {
          const loginPath = '/user/login';
          const onLoginPage = window.location.pathname === loginPath;

          if (!isHandling401) {
            isHandling401 = true;
            const { clearAuth } = useAuthStore.getState();
            clearAuth();

            // On the login page, 401 is expected (no/expired session) — do not show an error toast.
            if (!onLoginPage) {
              message.error(backendMessage || 'Your session has expired. Please login again.');

              const currentPath = window.location.pathname;
              const redirectPath =
                currentPath !== loginPath
                  ? `${loginPath}?redirect=${encodeURIComponent(currentPath)}`
                  : loginPath;

              history.replace(redirectPath);
            }

            setTimeout(() => { isHandling401 = false; }, 3000);
          }
          return;
        }

        if (status === 403) {
          const data = error.response.data || {};
          const anyOfRaw = data.permissionRequirementsAnyOf;
          const anyOf = Array.isArray(anyOfRaw)
            ? (anyOfRaw as unknown[]).filter(isPermissionRow)
            : [];

          if (anyOf.length >= 2) {
            showAdminPermissionAlternativesModal(anyOf);
            return;
          }

          if (backendMessage) {
            message.warning(backendMessage);
          } else {
            message.error('Access forbidden');
          }
          return;
        }

        if (status === 404) {
          message.error(backendMessage || `404 Not Found: ${fullUrl || url || 'Unknown endpoint'}`);
        } else {
          // 5xx and any other status: surface the server's message when present.
          message.error(backendMessage || `Response status: ${status} ${statusText}`);
        }
        return;
      }

      if (error.request) {
        message.error('None response! Please retry.');
        return;
      }

      message.error('Request error, please retry.');
    },
  },
  requestInterceptors: [
    (config: RequestOptions) => {
      const token = useAuthStore.getState().token || getAuthToken();
      // Resolve the current admin UI locale → short `lang` header (ko/zh/en)
      // sent on every request so the backend can localize responses.
      const lang = localeToLang(getLocale());

      config.params = sanitizeParams((config.params || {}) as Record<string, unknown>);
      config.headers = {
        Accept: 'application/json',
        lang,
        ...(config.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      return config;
    },
  ],
  responseInterceptors: [
    (response) => {
      const data = (response as any)?.data as ResponseStructure | undefined;

      // if (data?.status === 'error' || data?.status === 'failed') {
      //   message.error(data?.message || 'Request failed');
      // } else if (data?.success === false) {
      //   message.error(data?.errorMessage || 'Request failed');
      // }

      return response;
    },
  ],
};
